import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import dynamic from 'next/dynamic';
const SocialProof = dynamic(() => import('../../components/SocialProof'), { ssr: false });
const ContactUs = dynamic(() => import('../../components/ContactUs'), { ssr: false });
const Footer = dynamic(() => import('../../components/Footer'), { ssr: false });
import ServiceCard from '../../components/ServiceCard';
import services from '../../data/services/dynamic_services';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';


const groupServices = () => {
  const result = [];
  let i = 0;
  let toggleThree = true;

  while (i < services.length) {
    const count = toggleThree ? 3 : 2;
    result.push(services.slice(i, i + count));
    i += count;
    toggleThree = !toggleThree;
  }

  return result;
};

const ServiceAreasPage = () => {
  const base = 'https://modernbuildremodel.com';

  const service = {
      title: 'Service Areas by Remodeling Category',
      image: '/hero/best-home-remodeling-and-construction-services-in-los-angeles.webp',
      description:
        'Explore cities and ZIP codes in California where Modern Build provides expert home remodeling services. Free estimate: (323) 300-4130',
      link: '/service-areas',
    };
    
  const pageUrl = `${base}${service.link}`;
  const groupedServices = groupServices();

  // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": pageUrl }
    ]
  };

  // Service JSON-LD
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${pageUrl}#service`,
    "mainEntityOfPage": { "@id": `${pageUrl}#webpage` },
    "name": `${service.title} in Los Angeles, CA`,
    "serviceType": service.title.replace("/", "and"),
    "description": service.description,
    "url": pageUrl,
    "image": `${base}${service.image}`,
    "provider": { "@id": `${base}#localbusiness` },
    "areaServed": [
      { "@type": "AdministrativeArea", "name": "Los Angeles County, CA" },
      { "@type": "AdministrativeArea", "name": "Orange County, CA" },
      { "@type": "AdministrativeArea", "name": "Ventura County, CA" }
    ],
    "offers": [
      {
        "@type": "Offer",
        "url": pageUrl,
        "priceCurrency": "USD",
        "price": "0",
        "name": "Free Estimate",
        "description": "Get a free, no-obligation estimate for your project",
        "availability": "https://schema.org/InStock",
      },
      {
        "@type": "Offer",
        "url": `${pageUrl}#financing`,
        "name": "Financing Available",
        "description": "Flexible financing options with $0 down",
        "availability": "https://schema.org/InStock"
      }
    ]
  };
  
  return (
    <>
    <Head>
      <title>Service Areas by Remodeling Category | Modern Build</title>
      <meta name="robots" content="index, follow" />
      <meta name="title" content="Service Areas by Remodeling Category | Modern Build" />
      <meta name="description" content="Explore remodeling service categories offered by Modern Build. Click each category to view covered cities and ZIP codes in California." />
      <meta name="keywords" content="Service areas, remodeling services, California ZIP codes, home renovation coverage, Modern Build service zones" />
      <meta name="author" content="Modern Build Home Remodeling" />

      <link rel="canonical" href="https://modernbuildremodel.com/service-areas" />

      {/* Open Graph */}
      <meta property="og:title" content="Service Areas by Remodeling Category | Modern Build" />
      <meta property="og:description" content="Click to explore where we offer kitchen, bathroom, and full home remodeling services across California ZIP codes." />
      <meta property="og:image" content="https://modernbuildremodel.com/hero/best-home-remodeling-and-construction-services-in-los-angeles.webp" />
      <meta property="og:url" content="https://modernbuildremodel.com/service-areas" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Modern Build Home Remodeling" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Service Areas by Remodeling Category | Modern Build" />
      <meta name="twitter:description" content="Explore cities and ZIP codes in California where Modern Build provides expert home remodeling services." />
      <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-home-remodeling-and-construction-services-in-los-angeles.webp" />
      <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Service Areas | Modern Build',
                description: 'Find all California ZIP codes and cities where Modern Build provides remodeling services. Choose your area and start your renovation today.',
                slug: '/service-areas'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      </Head>


      <Header />
      <Hero category="servicesAreas" page="page" hasContentH1={false}/>

      <section className="services-section py-16 bg-background text-text">
        <div className="max-w-[1140px] mx-auto px-4">
          <div className="text-center mt-10 mb-8">
            <h2 className="text-4xl text-left tracking-[6px] uppercase text-primary font-light mb-4">
              Our Service Areas
            </h2>
            <p className="text-xl capitalize font-light text-left text-darkBackground font-gilda">
              Select a service below to see where we operate across California.
            </p>
          </div>

          {groupedServices.map((group, rowIndex) => (
            group.length === 1 ? (
              <div key={rowIndex} className="flex mt-8 justify-center">
                <div className="w-full sm:w-2/3 lg:w-1/2">
                  <ServiceCard
                    key={`${rowIndex}-0`}
                    imageSrc={group[0].imageSrc}
                    title={group[0].title}
                    description="View all cities and ZIP codes covered"
                    link={`/service-areas${group[0].link.replace('/services', '')}`}
                    height={group[0].height}
                  />
                </div>
              </div>
            ) : (
              <div
                key={rowIndex}
                className={`grid gap-8 mt-8 ${
                  group.length === 3
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2'
                }`}
              >
                {group.map((service, index) => (
                  <ServiceCard
                    key={`${rowIndex}-${index}`}
                    imageSrc={service.imageSrc}
                    title={service.title}
                    description="View all cities and ZIP codes covered"
                    link={`/service-areas${service.link.replace('/services', '')}`}
                    height={service.height}
                  />
                ))}
              </div>
            )
          ))}
        </div>
      </section>

      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
};

export default ServiceAreasPage;